import { SupabaseClient } from "@supabase/supabase-js";

export interface PendingImage {
  id: string;
  blob: Blob;
  localUrl: string;
}

function dataUriToBlob(dataUri: string): Blob {
  const [header, b64] = dataUri.split(",");
  const mime = header.split(":")[1].split(";")[0];
  const binary = atob(b64);
  const buffer = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) buffer[i] = binary.charCodeAt(i);
  return new Blob([buffer], { type: mime });
}

// Must run BEFORE style attributes are stripped so font-weight/font-style are readable
function resolveSemanticTags(body: HTMLElement): void {
  // Process innermost first (reverse) to avoid parent/child conflicts
  const bTags = Array.from(body.querySelectorAll("b, strong")).reverse();
  bTags.forEach((el) => {
    const fw = (el as HTMLElement).style.fontWeight;
    // Google Docs wraps ALL text in <b style="font-weight:normal"> — flatten those
    const isNonBold = fw === "normal" || fw === "400" || fw === "300" || fw === "200" || fw === "100";
    if (isNonBold) {
      el.replaceWith(...Array.from(el.childNodes));
    } else {
      const strong = body.ownerDocument.createElement("strong");
      strong.append(...Array.from(el.childNodes));
      el.replaceWith(strong);
    }
  });

  const iTags = Array.from(body.querySelectorAll("i, em")).reverse();
  iTags.forEach((el) => {
    const fs = (el as HTMLElement).style.fontStyle;
    if (fs === "normal") {
      el.replaceWith(...Array.from(el.childNodes));
    } else {
      const em = body.ownerDocument.createElement("em");
      em.append(...Array.from(el.childNodes));
      el.replaceWith(em);
    }
  });
}

function flattenWrappers(body: HTMLElement): void {
  Array.from(body.querySelectorAll("font, span, u, s, strike")).reverse().forEach((el) => {
    el.replaceWith(...Array.from(el.childNodes));
  });
}

function stripNoise(body: HTMLElement): void {
  body.querySelectorAll("meta, style, script, link, head").forEach((el) => el.remove());

  // ownerDocument — body is from DOMParser, not the live document
  const walker = body.ownerDocument.createTreeWalker(body, NodeFilter.SHOW_ELEMENT);
  const elements: Element[] = [];
  let node = walker.nextNode();
  while (node) { elements.push(node as Element); node = walker.nextNode(); }
  elements.forEach((el) => {
    el.removeAttribute("style");
    el.removeAttribute("class");
    el.removeAttribute("id");
    el.removeAttribute("dir");
    el.removeAttribute("lang");
    el.removeAttribute("data-highlight-colour");
    el.removeAttribute("data-font-name");
  });
}

function normaliseStructure(body: HTMLElement): void {
  // Unwrap <div> containers that hold only block-level children
  Array.from(body.querySelectorAll("div")).reverse().forEach((div) => {
    const onlyBlocks = Array.from(div.childNodes).every(
      (n) =>
        n.nodeType === Node.TEXT_NODE
          ? !n.textContent?.trim()
          : ["P","H1","H2","H3","H4","H5","H6","UL","OL","LI","BLOCKQUOTE","TABLE","TR","TD","TH","IMG"].includes((n as Element).tagName)
    );
    if (onlyBlocks) div.replaceWith(...Array.from(div.childNodes));
  });
}

// Google Docs (and some Word exports) emit ONE <ol>/<ul> per list item instead
// of a single list wrapping all <li> siblings — e.g.
//   <ol><li>First</li></ol><ol start="2"><li>Second</li></ol><ol start="3">...
// Every browser numbers each of those single-item lists as "1." independently,
// which is the root cause of published posts rendering "1. 1. 1. 1." instead
// of "1. 2. 3. 4.". Fix at the source: merge adjacent same-tag list elements
// (skipping whitespace-only text nodes between them) into one list before the
// markup is ever inserted into the editor or saved.
//
// Content managers frequently type a bare domain ("google.com") into the
// Insert Link prompt, expecting a normal external link. Without a scheme,
// document.execCommand("createLink") still creates the <a>, but the browser
// resolves the href as a path relative to the current admin page instead of
// navigating out to the intended site -- it looks identical in the editor
// and only breaks once someone actually clicks it. Auto-prepend https://
// for anything that isn't already an absolute URL, a known non-http scheme
// (mailto:, tel:), or a site-relative internal link/anchor, which must stay
// relative so the app's own router keeps handling it.
const KNOWN_SCHEME_RE = /^(https?:|mailto:|tel:|ftp:|\/\/)/i;

export function normalizeLinkUrl(rawUrl: string): string {
  const url = rawUrl.trim();
  if (!url) return url;
  if (KNOWN_SCHEME_RE.test(url)) return url;
  if (url.startsWith("/") || url.startsWith("#")) return url;
  return `https://${url}`;
}

// QA fix: only merge when at least one side of the pair has exactly one <li>
// — that's the actual Google-Docs split-list signature. Without this guard,
// two genuinely separate, fully-formed lists (e.g. a 3-item list immediately
// followed by an unrelated 2-item list, with no paragraph between them) were
// being force-merged into one continuous list, which is a content regression,
// not a fix. A single-item list is still merged into ANY adjacent same-tag
// list (including an already-multi-item one), so the Google Docs chain of
// N one-item lists — and a stray trailing one-item fragment after a normal
// list — both still fully self-heal.
export function mergeAdjacentLists(root: HTMLElement): void {
  const lists = Array.from(root.querySelectorAll("ol, ul"));
  const itemCount = (el: Element): number =>
    Array.from(el.children).filter((c) => c.tagName === "LI").length;

  lists.forEach((list) => {
    // Already absorbed into an earlier sibling list in a previous iteration
    if (!list.parentNode) return;

    const isBlankText = (n: ChildNode | null): n is Text =>
      !!n && n.nodeType === Node.TEXT_NODE && !n.textContent?.trim();

    let next: ChildNode | null = list.nextSibling;
    while (isBlankText(next)) {
      const blank = next;
      next = next.nextSibling;
      blank.remove();
    }

    while (
      next &&
      next.nodeType === Node.ELEMENT_NODE &&
      (next as Element).tagName === list.tagName &&
      (itemCount(list) === 1 || itemCount(next as Element) === 1)
    ) {
      const sibling = next as Element;
      while (sibling.firstChild) list.appendChild(sibling.firstChild);
      next = sibling.nextSibling;
      sibling.remove();
      while (isBlankText(next)) {
        const blank = next;
        next = next.nextSibling;
        blank.remove();
      }
    }
  });
}

// Pure string→string wrapper around mergeAdjacentLists for callers (and tests)
// that don't already have a parsed DOM handy.
export function normalizeListMarkup(html: string): string {
  if (!html) return html;
  const doc = new DOMParser().parseFromString(html, "text/html");
  mergeAdjacentLists(doc.body);
  return doc.body.innerHTML;
}

export interface CleanPasteResult {
  html: string;
  // Newly-created pending images from this paste only (data: URIs converted
  // to local blob: previews). Pure return value instead of a setState side
  // effect so the caller can both track them AND kick off their upload
  // immediately — never deferred to Save — without racing React state.
  images: PendingImage[];
}

export function cleanPastedHtml(html: string): CleanPasteResult {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const body = doc.body;

  // 1 — resolve b/i BEFORE style strip (Google Docs font-weight:normal fix)
  resolveSemanticTags(body);

  // 2 — strip noise tags and all style/class/id attrs
  stripNoise(body);

  // 3 — flatten wrapper tags (span, font, u, s)
  flattenWrappers(body);

  // 4 — normalise div wrappers → transparent
  normaliseStructure(body);

  // 4a — merge adjacent single-item <ol>/<ul> siblings (Google Docs paste shape)
  // into one list so numbering renders 1,2,3,4 instead of 1,1,1,1
  mergeAdjacentLists(body);

  // 4b — flatten <p> inside <li> (Google Docs wraps each list item text in a <p>)
  body.querySelectorAll("li > p").forEach((p) => {
    p.replaceWith(...Array.from(p.childNodes));
  });

  // 5 — handle images
  const newPending: PendingImage[] = [];
  body.querySelectorAll("img").forEach((img) => {
    const w = parseInt(img.getAttribute("width") || "100");
    const h = parseInt(img.getAttribute("height") || "100");
    if (w <= 1 || h <= 1) { img.remove(); return; }

    if (img.src.startsWith("data:")) {
      const blob = dataUriToBlob(img.src);
      const id = `img-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      const localUrl = URL.createObjectURL(blob);
      img.src = localUrl;
      img.dataset.pendingId = id;
      img.setAttribute("loading", "lazy");
      newPending.push({ id, blob, localUrl });
    } else if (img.src.includes("googleusercontent.com") || img.src.includes("docs.google.com")) {
      // Google Docs images require auth — replace with a re-upload prompt placeholder
      const placeholder = body.ownerDocument.createElement("p");
      placeholder.textContent = "[Image from Google Docs — please re-upload via the toolbar]";
      placeholder.style.cssText = "color:#b45309;font-style:italic;";
      img.replaceWith(placeholder);
    } else {
      img.setAttribute("loading", "lazy");
    }
  });

  // 6 — collapse consecutive <br> tags inside paragraphs
  body.querySelectorAll("p, li").forEach((el) => {
    const brs = Array.from(el.querySelectorAll("br"));
    let prevWasBr = false;
    brs.forEach((br) => {
      if (prevWasBr) br.remove();
      prevWasBr = br.nextSibling?.nodeType === Node.ELEMENT_NODE && (br.nextSibling as Element).tagName === "BR";
    });
  });

  // 7 — remove empty paragraphs/headings: covers <p></p>, <p><br></p>,
  // <p>&nbsp;</p>. Also covers h1-h6 — Word/Google Docs clipboard HTML
  // sometimes includes a stray empty <h1></h1> right after the
  // <!--StartFragment--> marker (confirmed live on a real published post),
  // which used to survive paste-cleaning untouched since this selector only
  // ever covered p/div, and render as unexplained blank space above the body.
  body.querySelectorAll("p, div, h1, h2, h3, h4, h5, h6").forEach((el) => {
    const text = el.textContent ?? "";
    const onlyBr = el.childNodes.length === 1 && (el.firstChild as Element)?.tagName === "BR";
    const onlyNbsp = text.replace(/ /g, "").trim() === "";
    if ((onlyBr || onlyNbsp) && !el.querySelector("img")) el.remove();
  });

  return { html: body.innerHTML, images: newPending };
}

// Single-image upload, shared by the immediate paste/insert upload path
// (RichTextEditor) and the save-time safety net below (uploadPendingImages).
// Returns the permanent public URL, or null if the upload failed — callers
// must NEVER fall back to persisting the blob: URL when this returns null.
export async function uploadImageBlob(
  blob: Blob,
  slug: string,
  supabase: SupabaseClient,
  indexHint: number = 0
): Promise<string | null> {
  const ext = blob.type.includes("png") ? "png" : blob.type.includes("gif") ? "gif" : "jpg";
  const path = `${slug}/${Date.now()}-${indexHint}.${ext}`;
  try {
    const { error } = await supabase.storage
      .from("blog-images")
      .upload(path, blob, { contentType: blob.type, upsert: true });
    if (error) return null;
    const { data } = supabase.storage.from("blog-images").getPublicUrl(path);
    return data?.publicUrl ?? null;
  } catch (err) {
    // Network-level failures (offline, DNS, CORS, etc.) can reject the
    // upload promise instead of resolving it with { error } — treat that
    // identically to an API-level upload error. Without this, every caller
    // is exposed to an unhandled rejection: RichTextEditor's immediate
    // upload path calls this fire-and-forget (`void uploadAndSwap(...)`),
    // so a thrown exception here would never reach the img/pendingImages
    // failure handling — the image would stay "uploading" forever and
    // Save/Publish/Schedule/autosave would stay blocked for the rest of the
    // session (they all gate on pendingImages.length > 0) with nothing but
    // a silent console error as a trace. And uploadPendingImages' caller
    // (handleSave/handleSchedule) awaits this with no try/catch either, so
    // a throw there would skip setSaving(false) and leave Save stuck
    // showing "Saving..." forever.
    console.error("[uploadImageBlob] upload threw", err);
    return null;
  }
}

// Save-time safety net: RichTextEditor now uploads images immediately on
// paste/insert (see handleImageButton / handlePaste), so pendingImages should
// normally be empty by the time Save is clicked. This still runs in case an
// upload was still in flight, or failed and needs a retry, when Save fires —
// it must never leave a blob: URL in the saved HTML, so on failure we strip
// the broken <img> rather than persist an unresolvable blob: src.
export async function uploadPendingImages(
  pendingImages: PendingImage[],
  contentHtml: string,
  slug: string,
  supabase: SupabaseClient
): Promise<string> {
  let html = contentHtml;
  for (let i = 0; i < pendingImages.length; i++) {
    const { blob, localUrl } = pendingImages[i];
    const publicUrl = await uploadImageBlob(blob, slug, supabase, i);
    if (publicUrl) {
      html = html.split(localUrl).join(publicUrl);
    } else if (html.includes(localUrl)) {
      // Upload failed — never persist a blob: URL. Drop the broken <img> tag
      // for this specific src so we don't ship a dead image to the live site.
      const doc = new DOMParser().parseFromString(html, "text/html");
      doc.body.querySelectorAll(`img[src="${localUrl}"]`).forEach((img) => img.remove());
      html = doc.body.innerHTML;
    }
    URL.revokeObjectURL(localUrl);
  }
  return html;
}

// Absolute last-resort guard used right before any DB write (manual save or
// autosave): a blob: URL must never reach content_html. If one slips through
// for any reason, strip that <img> rather than persist a URL that can only
// ever work in the tab that created it.
export function stripUnresolvedBlobImages(html: string): string {
  if (!html || !html.includes('src="blob:')) return html;
  const doc = new DOMParser().parseFromString(html, "text/html");
  doc.body.querySelectorAll('img[src^="blob:"]').forEach((img) => img.remove());
  return doc.body.innerHTML;
}

// Strips a single leading <h1>...</h1> from HTML body content when the
// rendering surface already shows the title as its own dedicated <h1>
// element (e.g. BlogPost.tsx renders post.title as an H1, so a
// content_html that also opens with its own <h1> duplicates it and
// creates a "two H1s" / skipped-heading-level structure). Regex-based
// (not DOMParser) so it's safe to call during SSR, not just in the
// browser. Only strips a *leading* H1 -- an H1 appearing later in the
// body is left alone, since that's a real authoring issue to flag, not
// something this helper should silently hide.
export function stripLeadingH1Html(html: string): string {
  if (!html) return html;
  return html.replace(/^\s*<h1\b[^>]*>[\s\S]*?<\/h1>\s*/i, "");
}
